/**
 * Checkout lead capture: POST name + email to api after validation (RudderStack downstream).
 */

/** Stores JSON fingerprint of last successful payload (normalized email + name fields). */
const STORAGE_KEY_LAST_SENT = 'checkout_lead_capture_last_sent';

/** Single-flight: blur + errors watcher (and similar) can fire in the same turn; dedupe here. */
let pendingLeadCapture = null;

function getCookie(name) {
	const value = `; ${document.cookie}`;
	const parts = value.split(`; ${name}=`);
	if (parts.length === 2) {
		return parts.pop().split(';').shift();
	}
}

/**
 * @param {Record<string, unknown>} body
 * @returns {string}
 */
export function fingerprintFromLeadCaptureBody(body) {
	if (body.name !== undefined) {
		return JSON.stringify({
			email: String(body.email).trim().toLowerCase(),
			name: String(body.name).trim(),
		});
	}
	return JSON.stringify({
		email: String(body.email).trim().toLowerCase(),
		first_name: String(body.first_name ?? '').trim(),
		last_name: String(body.last_name ?? '').trim(),
	});
}

/**
 * @param {{ email: string, first_name?: string, last_name?: string, name?: string }} payload
 */
function buildLeadCaptureBody(payload) {
	return payload.name
		? { email: payload.email, name: payload.name }
		: { email: payload.email, first_name: payload.first_name, last_name: payload.last_name };
}

function readLastSentFingerprint() {
	if (typeof sessionStorage === 'undefined') {
		return null;
	}
	return sessionStorage.getItem(STORAGE_KEY_LAST_SENT);
}

function writeLastSentFingerprint(fp) {
	sessionStorage.setItem(STORAGE_KEY_LAST_SENT, fp);
}

export function getCheckoutLeadCaptureUrl() {
	if (typeof window !== 'undefined' && window.ECOM && window.ECOM.CHECKOUT_LEAD_CAPTURE_URL) {
		return window.ECOM.CHECKOUT_LEAD_CAPTURE_URL;
	}

	return '/api/checkout/lead-capture';
}

/**
 * @param {{ email: string, first_name?: string, last_name?: string, name?: string }} payload
 */
export function maybeSubmitCheckoutLeadCapture(payload) {
	if (typeof sessionStorage === 'undefined') {
		return Promise.resolve();
	}

	const body = buildLeadCaptureBody(payload);
	const fp = fingerprintFromLeadCaptureBody(body);
	if (fp === readLastSentFingerprint()) {
		return Promise.resolve();
	}
	if (pendingLeadCapture) {
		return pendingLeadCapture.then(() => maybeSubmitCheckoutLeadCapture(payload));
	}

	const url = getCheckoutLeadCaptureUrl();
	const meta = typeof document !== 'undefined' && document.head && document.head.querySelector('meta[name="csrf-token"]');
	const token = meta ? meta.content : '';

	pendingLeadCapture = fetch(url, {
		method: 'POST',
		credentials: 'same-origin',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
			'X-CSRF-TOKEN': token,
			'X-XSRF-TOKEN': getCookie('XSRF-TOKEN') || '',
			'X-Requested-With': 'XMLHttpRequest',
		},
		body: JSON.stringify(body),
	})
		.then((res) => {
			if (res.ok) {
				writeLastSentFingerprint(fp);
			}
		})
		.catch(() => {})
		.finally(() => {
			pendingLeadCapture = null;
		});

	return pendingLeadCapture;
}

/**
 * Fast-checkout Petite-Vue: same name + email checks as validateContact (name/email lines only).
 *
 * @param {{ email?: string, name?: string }} formUser
 */
export function runFastCheckoutLeadCaptureIfReady(formUser) {
	const emailFormat = /^([a-zA-Z0-9_.+-])+\@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/;
	const email = formUser && formUser.email;
	const name = formUser && formUser.name;
	if (!email || !name) {
		return;
	}
	const em = String(email).trim();
	const nm = String(name).trim();
	if (!em || !emailFormat.test(em) || em.length > 255) {
		return;
	}
	if (nm.length <= 0 || nm.length > 255) {
		return;
	}
	maybeSubmitCheckoutLeadCapture({ email: em, name: nm });
}

if (typeof window !== 'undefined') {
	window.checkoutLeadCapture = {
		maybeSubmitCheckoutLeadCapture,
		getCheckoutLeadCaptureUrl,
		runFastCheckoutLeadCaptureIfReady,
	};
}
